/*
 *   File : forgot-password.js
 *   Author : https://evoqins.com
 *   Description : forgot password component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useRef, useState } from "react";
import { Input, OtpInput, Password } from "../../Components/FormElements";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PrimaryButton } from "../../Components/Buttons";
import Icon from "../../Components/icons";
import { APIService } from "../../Service";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const otpInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // STEP 1 — Email
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // STEP 2 — OTP
  const [otpValue, setOtpValue] = useState("");
  const [focusedInput, setFocusedInput] = useState(2);
  const [confirmOTP, setConfirmOTP] = useState([]);
  const [otpError, setOtpError] = useState("");
  const [counter, setCounter] = useState(10);
  const [startCounter, setStartCounter] = useState(false);

  // STEP 3 — Password
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [confirmPassError, setConfirmPassError] = useState("");
  const [tempToken, setTempToken] = useState("");

  // -------------------------------
  // OTP Auto focus
  // -------------------------------
  useEffect(() => {
    if (otpInputRef && otpInputRef.current) {
      otpInputRef.current[0]?.focus();
    }
  }, []);

  // OTP Timer
  useEffect(() => {
    if (step !== 2) return;

    setCounter(10);

    const t = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 0) {
          clearInterval(t);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [step, startCounter]);

  useEffect(() => {
    setEmailError("");
  }, [email]);

  useEffect(() => {
    setPasswordError("");
  }, [password]);

  useEffect(() => {
    setConfirmPassError("");
  }, [confirmPass]);

  useEffect(() => {
    setOtpError("");
  }, [otpValue]);

  // STEP 1 — Email Validation
  const _validateEmailStep = () => {
    setEmailError("");

    const emailRegex = /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError("Enter a valid email address");
      return;
    }

    _getOTP();
  };

  // API - Get OTP
  const _getOTP = () => {
    setLoading(true);
    let is_post = true;
    let url = "auth/forgot-password";
    let data = {
      email: email,
    };

    APIService(is_post, url, data).then((response) => {
      if (response.status_code === 200) {
        toast.dismiss();
        toast.success(response.message, {
          type: "success",
          className: "e-toast",
        });
        setStep(2);
      } else {
        toast.dismiss();
        toast.error(response.message, {
          type: "error",
          className: "e-toast",
        });
      }
      setLoading(false);
    });
  };

  // STEP 2 — OTP Validation
  const _validateOTPStep = () => {
    setOtpError("");

    if (otpValue.length === 0) {
      setOtpError("OTP is required");
      return;
    }
    if (otpValue.length < 4) {
      setOtpError("Enter the complete 4-digit OTP");
      return;
    }

    _verifyOTP();
  };

  // API - Verify OTP
  const _verifyOTP = () => {
    setLoading(true);
    let is_post = true;
    let url = "auth/verify-otp";
    let data = {
      email: email,
      otp: otpValue,
      type: "RESET_PASSWORD",
    };

    APIService(is_post, url, data).then((response) => {
      if (response.status_code === 200) {
        setTempToken(response.temp_token);
        setStep(3);
      } else {
        if (otpInputRef.current && otpInputRef.current[0]) {
          otpInputRef.current[0].focus();
        }
        toast.dismiss();
        toast.error(response.message, {
          type: "error",
          className: "e-toast",
        });
      }
      setOtpValue("");
      setConfirmOTP([]);
      setLoading(false);
    });
  };

  // STEP 3 — Reset Password Validation
  const _validatePasswordStep = () => {
    setPasswordError("");
    setConfirmPassError("");

    if (!password.trim()) {
      setPasswordError("Password is required");
      setConfirmPassError("Confirm Password is required");
      return;
    }
    if (password.trim().length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (!confirmPass.trim()) {
      setConfirmPassError("Confirm your password");
      return;
    }
    if (password !== confirmPass) {
      setPasswordError("Passwords do not match");
      setConfirmPassError("Passwords do not match");
      return;
    }

    _updatePassword();
  };

  // API - Update Password
  const _updatePassword = () => {
    setLoading(true);
    let is_post = true;
    let url = "auth/reset-password";
    let data = {
      temp_token: tempToken,
      confirm_password: confirmPass,
      password: password,
    };

    APIService(is_post, url, data).then((response) => {
      if (response.status_code === 200) {
        navigate("/");
        toast.dismiss();
        toast.success(response.message, {
          type: "success",
          className: "e-toast",
        });
      } else {
        toast.dismiss();
        toast.error(response.message, {
          type: "error",
          className: "e-toast",
        });
      }
      setLoading(false);
    });
  };

  // Handle OTP Input
  const _handleOTP = (e) => {
    if (loading !== true) {
      const input_value = e.target.value;
      if (input_value.length <= 4) {
        setOtpValue(input_value);
        const chars = [];
        for (let i = 0; i < input_value.length; i++) {
          chars.push(input_value.charAt(i));
        }
        setConfirmOTP(chars);
      }
    }
  };

  // API - Resend OTP
  const _handleResendOTP = () => {
    setOtpValue("");
    setConfirmOTP([]);
    setCounter(10);
    setStartCounter(!startCounter);

    let is_post = true;
    let url = "auth/resend";
    let data = {
      email: email,
      type: "RESET_PASSWORD",
    };

    APIService(is_post, url, data).then((response) => {
      if (response.status_code === 200) {
        setOtpValue("");
        setConfirmOTP([]);
        toast.success(response.message, {
          type: "success",
          className: "e-toast",
        });
      } else {
        toast.dismiss();
        toast.error(response.message, {
          type: "error",
          className: "e-toast",
        });
      }
    });
  };

  //Handle Back Navigation
  const _handleBackNav = () => {
    if (step === 3) {
      setConfirmPass("");
      setPassword("");
      setStep(1);
    } else if (step === 2) {
      setOtpValue("");
      setConfirmOTP([]);
      setStep(1);
    } else {
      navigate("/login");
    }
  };

  return (
    <>
      <PrimaryButton
        ClassName={"bg-transparent pe-0 ps-22"}
        onPress={_handleBackNav}
        icon="arrow-left"
      />

      {/* STEP 1 — Email */}
      {step === 1 && (
        <div className="e-glass-card p-24 d-flex flex-column w-100 rounded-30">
          <h3 className="fs-24 fw-600 mb-0 pb-24">{"Reset Password"}</h3>

          <div className="d-flex flex-column row-gap-2">
            <Input
              id="1"
              type="email"
              label="Registered email"
              placeholder="Enter email"
              require={true}
              value={email}
              error={emailError}
              className="w-100 px-3 py-10"
              handleChange={setEmail}
              onKeyPress={_validateEmailStep}
              autoFocus={true}
            />
          </div>

          <div className="w-100 pt-2">
            <PrimaryButton
              ClassName="rounded-pill bg-midnight-blue lh-1 py-15_5 text-white w-100 fs-14 fw-600"
              name="Verify"
              loading={loading}
              onPress={_validateEmailStep}
            />
          </div>
        </div>
      )}

      {/* STEP 2 — OTP */}
      {step === 2 && (
        <div className="e-glass-card p-24 d-flex flex-column w-100 rounded-30 rg-24">
          <h3 className="fs-24 fw-600 mb-0 pb-24">{"OTP Verification"}</h3>

          <div className="d-flex align-items-center column-gap-3">
            <div className="d-flex flex-column">
              <OtpInput
                label="Enter OTP"
                ref={otpInputRef}
                id="otp"
                value={otpValue}
                spellcheck="false"
                error={otpError}
                require={true}
                focused_input={focusedInput}
                confirm_pin={confirmOTP}
                onFocus={() => setFocusedInput(2)}
                onBlur={() => setFocusedInput(0)}
                onKeyPress={_validateOTPStep}
                onChange={_handleOTP}
              />
            </div>

            <p className="fw-400 fs-12 lh-16 text-granite-gray mb-0 pt-1 ">
              {` 00:${counter}s`}
            </p>
          </div>

          <p className="fw-400 fs-12 lh-16 text-granite-gray mb-0">
            {"Didn't get an OTP?"}
            <PrimaryButton
              disabled={counter !== 0}
              onPress={_handleResendOTP}
              ClassName="fw-400 fs-12 lh-16 bg-transparent px-0 text-facebook-blue ps-1"
              name={"Resend OTP"}
            />
          </p>

          <div className="w-100">
            <PrimaryButton
              ClassName="rounded-pill bg-midnight-blue lh-1 py-15_5 text-white w-100 fs-14 fw-600"
              name="Verify"
              loading={loading}
              onPress={_validateOTPStep}
            />
          </div>
        </div>
      )}

      {/* STEP 3 — Set New Password */}
      {step === 3 && (
        <div className="e-glass-card p-24 d-flex flex-column w-100 rounded-30">
          <h3 className="fs-24 fw-600 mb-0 pb-24">{"Set new password"}</h3>

          <div className="d-flex flex-column row-gap-2">
            <Password
              id="2"
              label="Password"
              placeholder="Enter Password"
              require={true}
              value={password}
              error={passwordError}
              className="w-100 px-3 py-10"
              handleChange={setPassword}
              onKeyPress={_validatePasswordStep}
              autoFocus={true}
            />

            <Password
              id="3"
              label="Confirm Password"
              placeholder="Confirm Password"
              require={true}
              value={confirmPass}
              error={confirmPassError}
              onKeyPress={_validatePasswordStep}
              className="w-100 px-3 py-10"
              handleChange={setConfirmPass}
            />
          </div>

          <div className="w-100 pt-2">
            <PrimaryButton
              ClassName="rounded-pill bg-midnight-blue lh-1 py-15_5 text-white w-100 fs-14 fw-600"
              name="Verify"
              loading={loading}
              onPress={_validatePasswordStep}
            />
          </div>
        </div>
      )}
    </>
  );
}
